// ============================================================
// Team + audit log API — /v2/orgs/current/* (members, invites, audit log)
// on the v2 client; invite acceptance + org switching on the v1 auth API.
// ============================================================

import type { APIResponse } from '@yourownmetric/shared'

import { apiSlice } from './apiSlice'
import { V2_BASE_URL, v2Api } from './v2Api'
import {
    auditQuery,
    type AuditEntry,
    type AuditFilterInput,
    type AuditPage,
    type InvitePreview,
    type IssuedInvite,
    type OrgSummary,
    type PartnerAccess,
    type TeamInvite,
    type TeamMember,
} from './teamTypes'

interface Envelope<T> {
    success: boolean
    data: T
    meta?: { next_cursor: string | null; retention_days: number }
}

const ORG = '/orgs/current'

export const teamApi = v2Api
    .enhanceEndpoints({ addTagTypes: ['Org', 'Members', 'Invites', 'AuditLog', 'PartnerAccess'] })
    .injectEndpoints({
        endpoints: builder => ({
            getOrg: builder.query<OrgSummary, void>({
                query: () => ORG,
                transformResponse: (r: Envelope<OrgSummary>) => r.data,
                providesTags: ['Org'],
            }),
            setRequireMfa: builder.mutation<void, boolean>({
                query: requireMfa => ({
                    url: ORG,
                    method: 'PATCH',
                    body: { require_mfa: requireMfa },
                }),
                invalidatesTags: ['Org', 'AuditLog'],
            }),
            listMembers: builder.query<TeamMember[], void>({
                query: () => `${ORG}/members`,
                transformResponse: (r: Envelope<TeamMember[]>) => r.data,
                providesTags: ['Members'],
            }),
            listInvites: builder.query<TeamInvite[], void>({
                query: () => `${ORG}/invites`,
                transformResponse: (r: Envelope<TeamInvite[]>) => r.data,
                providesTags: ['Invites'],
            }),
            createInvite: builder.mutation<IssuedInvite, { email: string; role: string }>({
                query: body => ({ url: `${ORG}/invites`, method: 'POST', body }),
                transformResponse: (r: Envelope<IssuedInvite>) => r.data,
                invalidatesTags: ['Invites', 'Org', 'AuditLog'],
            }),
            resendInvite: builder.mutation<IssuedInvite, string>({
                query: id => ({
                    url: `${ORG}/invites/${encodeURIComponent(id)}/resend`,
                    method: 'POST',
                }),
                transformResponse: (r: Envelope<IssuedInvite>) => r.data,
                invalidatesTags: ['Invites', 'AuditLog'],
            }),
            revokeInvite: builder.mutation<void, string>({
                query: id => ({
                    url: `${ORG}/invites/${encodeURIComponent(id)}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Invites', 'Org', 'AuditLog'],
            }),
            changeRole: builder.mutation<void, { userId: string; role: string }>({
                query: ({ userId, role }) => ({
                    url: `${ORG}/members/${encodeURIComponent(userId)}`,
                    method: 'PATCH',
                    body: { role },
                }),
                invalidatesTags: ['Members', 'AuditLog'],
            }),
            removeMember: builder.mutation<void, string>({
                query: userId => ({
                    url: `${ORG}/members/${encodeURIComponent(userId)}`,
                    method: 'DELETE',
                }),
                invalidatesTags: ['Members', 'Org', 'AuditLog'],
            }),
            transferOwnership: builder.mutation<void, string>({
                query: userId => ({
                    url: `${ORG}/transfer-ownership`,
                    method: 'POST',
                    body: { user_id: userId },
                }),
                invalidatesTags: ['Members', 'Org', 'AuditLog'],
            }),
            leaveOrg: builder.mutation<void, void>({
                query: () => ({ url: `${ORG}/leave`, method: 'POST' }),
            }),
            getPartnerAccess: builder.query<PartnerAccess, void>({
                query: () => `${ORG}/partner-access`,
                transformResponse: (r: Envelope<PartnerAccess>) => r.data,
                providesTags: ['PartnerAccess'],
            }),
            revokePartnerAccess: builder.mutation<void, void>({
                query: () => ({ url: `${ORG}/partner-access`, method: 'DELETE' }),
                invalidatesTags: ['PartnerAccess', 'Org', 'AuditLog'],
            }),
            getAuditLog: builder.query<AuditPage, AuditFilterInput>({
                query: f => `${ORG}/audit-logs?${auditQuery(f)}`,
                transformResponse: (r: Envelope<AuditEntry[]>) => ({
                    entries: r.data,
                    next_cursor: r.meta?.next_cursor ?? null,
                    retention_days: r.meta?.retention_days ?? 0,
                }),
                providesTags: ['AuditLog'],
            }),
        }),
    })

export const {
    useGetOrgQuery,
    useSetRequireMfaMutation,
    useListMembersQuery,
    useListInvitesQuery,
    useCreateInviteMutation,
    useResendInviteMutation,
    useRevokeInviteMutation,
    useChangeRoleMutation,
    useRemoveMemberMutation,
    useTransferOwnershipMutation,
    useLeaveOrgMutation,
    useGetAuditLogQuery,
    useGetPartnerAccessQuery,
    useRevokePartnerAccessMutation,
    useLazyGetAuditLogQuery,
} = teamApi

/** Download the filtered audit log as CSV (a file the browser saves). */
export async function downloadAuditCsv(token: string, f: AuditFilterInput): Promise<void> {
    const res = await fetch(
        `${V2_BASE_URL}${ORG}/audit-logs?${auditQuery({ ...f, cursor: null }, { format: 'csv' })}`,
        {
            headers: { Authorization: `Bearer ${token}` },
        },
    )
    if (!res.ok) {
        throw new Error(`Export failed (${res.status})`)
    }
    const url = URL.createObjectURL(await res.blob())
    const a = document.createElement('a')
    a.href = url
    a.download =
        /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ??
        'audit-log.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}

// ---------- v1 auth: invites + org switching (responses camelCased by apiSlice) ----------

export interface SessionResponse {
    token: string
    user: { id: string; email: string; name: string; isSuperAdmin?: boolean }
    tenant: { id: string; role: string } | null
    isNewUser?: boolean
}

export interface InvitePreviewCamel {
    orgName: string
    email: string
    role: InvitePreview['role']
    inviterName: string | null
    expiresAt: string
    status: InvitePreview['status']
    accountExists: boolean
}

export interface MeResponse {
    id: string
    email: string
    name: string
    mfaEnabled: boolean
    emailVerified: boolean
    tenants: Array<{ id: string; name: string; slug: string; plan: string; role: string }>
}

export const inviteAuthApi = apiSlice.injectEndpoints({
    endpoints: builder => ({
        getInvitePreview: builder.query<InvitePreviewCamel, string>({
            query: token => `/auth/invites/${encodeURIComponent(token)}`,
            transformResponse: (r: APIResponse<InvitePreviewCamel>) => r.data!,
        }),
        acceptInvite: builder.mutation<
            SessionResponse,
            { token: string; name?: string; password?: string }
        >({
            query: body => ({ url: '/auth/invites/accept', method: 'POST', body }),
            transformResponse: (r: APIResponse<SessionResponse>) => r.data!,
        }),
        switchTenant: builder.mutation<SessionResponse, string>({
            query: tenantId => ({ url: '/auth/switch-tenant', method: 'POST', body: { tenantId } }),
            transformResponse: (r: APIResponse<SessionResponse>) => r.data!,
        }),
        getMe: builder.query<MeResponse, void>({
            query: () => '/auth/me',
            transformResponse: (r: APIResponse<MeResponse>) => r.data!,
        }),
    }),
})

export const {
    useGetInvitePreviewQuery,
    useAcceptInviteMutation,
    useSwitchTenantMutation,
    useGetMeQuery,
} = inviteAuthApi
